import {default as cx} from "classnames"
import {useState} from "react"
import {Dropdown} from "react-bootstrap"

import {OSRModal} from "../OSRModal"
import {PreceptorOrientationDetails} from "./PreceptorOrientationDetails"
import {getJSDate, scoreBadge, ORIENTATION_STATUS} from "../../utilities/general"
import {useStableNavigate} from "../../utilities/StableNavigateContext"

export const PreceptorOrientationSummary = ({orientation}) => {
    const navigate = useStableNavigate()
    const [showDetails, setShowDetails] = useState(false)
    const osrMax = scoreBadge(orientation.percent_of_max_osr_score, 100)
    const futureStartDate = new Date() < getJSDate(orientation.start_date)
    const reportDue = orientation.missing_osr_weeks.length > 0
    const osrLink = `/surveys/orientation-status-report-selection?orientationId=${orientation.id}`
    const latestOsrs = orientation.latest_week_osrs ?? []

    return (
        <div className="card mt-4">
            <div className="card-header flex-column flex-sm-row align-items-stretch align-items-sm-center py-4 py-sm-0 gap-3 gap-sm-4">
                <div className="card-title text-dark fw-bold gap-4 flex-fill m-0">
                    <div className="d-flex flex-row align-items-start align-items-sm-center gap-2 flex-fill">
                        <div className="d-flex flex-column fs-5 flex-fill">
                            <div className="d-flex flex-row">{orientation.name}</div>
                            <div className="d-flex flex-row flex-wrap gap-2 gap-sm-3 align-items-center">
                                <span className="text-dark fw-bold d-block fs-7">
                                    Week{" "}
                                    {orientation.current_week_of_hire === " "
                                        ? "N/A"
                                        : orientation.current_week_of_hire}
                                    : {orientation.where_do_you_think_they_are_in_their_orientation}
                                </span>
                                {!futureStartDate && (
                                    <>
                                        <span
                                            className={cx("fw-semibold fs-7", {
                                                "text-danger": !orientation.osr_submission_status,
                                            })}
                                        >
                                            {orientation.osr_submission_status ? (
                                                <i className="fas fa-circle-check text-success me-1 fs-5"></i>
                                            ) : (
                                                <i className="fas fa-exclamation-triangle text-danger me-1 fs-5"></i>
                                            )}
                                            OSR
                                        </span>
                                        <span
                                            className={cx("fw-semibold fs-7", {
                                                "text-danger": !orientation.nhsr_submission_status,
                                            })}
                                        >
                                            {orientation.nhsr_submission_status ? (
                                                <i className="fas fa-circle-check text-success me-1 fs-5"></i>
                                            ) : (
                                                <i className="fas fa-exclamation-triangle text-danger me-1 fs-5"></i>
                                            )}
                                            NHSR
                                        </span>
                                    </>
                                )}
                                {orientation.orientation_status_id === ORIENTATION_STATUS.PAUSED && (
                                    <span className="badge badge-light-warning fw-bold fs-7 ms-2">
                                        <i className="fas fa-pause me-1 fs-5"></i>
                                        Paused
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="d-flex flex-row align-items-center ms-2 ms-sm-4">
                            {osrMax.score && (
                                <div className={cx("badge px-4 py-3", osrMax.background, osrMax.textColor)}>
                                    {osrMax.score}%
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="card-toolbar gap-2 gap-sm-4 w-100 w-sm-auto">
                    <button
                        className={cx(
                            "btn btn-light-primary rotate collapsible justify-content-center flex-fill flex-sm-grow-0",
                            {collapsed: !showDetails}
                        )}
                        onClick={() => setShowDetails(!showDetails)}
                    >
                        Progress
                        <span className="d-flex flex-center rotate-180 ms-3">
                            <i className="fas fa-arrow-circle-down fs-3"></i>
                        </span>
                    </button>
                    <Dropdown role="menuitem">
                        <Dropdown.Toggle
                            aria-controls={"orientee_actions"}
                            id="dropdown_actions"
                            className="btn-light-primary p-3 no-caret"
                            disabled={futureStartDate}
                        >
                            <span>Actions</span>
                            {reportDue && (
                                <span className="position-absolute top-0 start-100 translate-middle ">
                                    <i className="fas fa-circle text-danger"></i>
                                </span>
                            )}
                        </Dropdown.Toggle>
                        <Dropdown.Menu as={"ul"} className="p-4 w-250px border-1" id="orientee_actions" role={"menu"}>
                            {reportDue && (
                                <>
                                    <Dropdown.Header as={"div"} className="fs-5 fw-bold px-3 py-0 text-dark">
                                        Due
                                    </Dropdown.Header>
                                    {orientation.missing_osr_weeks.map((week) => (
                                        <Dropdown.Item
                                            as={"li"}
                                            className="menu-item py-0"
                                            role="menuitem"
                                            key={`weekId${week.id}`}
                                        >
                                            <button
                                                className="btn btn-link menu-link w-100"
                                                onClick={() => {
                                                    navigate(`${osrLink}&orientationWeekId=${week.id}`)
                                                }}
                                            >
                                                <span className="menu-title px-3">
                                                    OSR Week {week.orientation_week_num}
                                                </span>
                                            </button>
                                        </Dropdown.Item>
                                    ))}
                                    <div className="separator separator-dotted my-2"></div>
                                </>
                            )}
                            {latestOsrs?.length > 0 && (
                                <>
                                    <Dropdown.Header as={"div"} className="fs-5 fw-bold px-3 py-0 text-dark">
                                        View
                                    </Dropdown.Header>
                                    <Dropdown.Item as={"li"} className="menu-item py-0" role="menuitem">
                                        <OSRModal
                                            osrs={latestOsrs}
                                            orienteeName={orientation?.name ?? null}
                                            hideTextAnswers={false}
                                            showWeek={true}
                                        />
                                    </Dropdown.Item>
                                    <div className="separator separator-dotted my-2"></div>
                                </>
                            )}
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
            </div>
            {showDetails && <PreceptorOrientationDetails orientationId={orientation.id} />}
        </div>
    )
}
